import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, pollJob, jobErrorMessage, newIdemKey, idemHeaders } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Loader2, ChevronLeft, ChevronRight, CalendarDays, List, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { mediaFileUrl } from "./constants";

const STATUS_COLORS = {
  Scheduled: "bg-emerald-100 text-emerald-700", "Ready to Schedule": "bg-slate-100 text-slate-600",
  Sending: "bg-indigo-100 text-indigo-700", Published: "bg-[#DCFCE7] text-[#166534]",
  Failed: "bg-red-100 text-red-700", Canceled: "bg-gray-100 text-gray-400",
  "Export Required": "bg-amber-100 text-amber-700", "Partially Published": "bg-amber-100 text-amber-700",
};
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function Calendar() {
  const nav = useNavigate();
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const [slots, setSlots] = useState(null);
  const [platform, setPlatform] = useState("all");
  const [status, setStatus] = useState("all");
  const [sel, setSel] = useState(null);
  const [reschedAt, setReschedAt] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const start = ymd(new Date(cursor.getFullYear(), cursor.getMonth(), 1)) + "T00:00:00+00:00";
    const end = ymd(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 7)) + "T23:59:59+00:00";
    const params = new URLSearchParams({ start, end });
    if (platform !== "all") params.set("platform", platform);
    if (status !== "all") params.set("status", status);
    api.get(`/marketing/calendar?${params}`).then((r) => setSlots(r.data)).catch(() => setSlots(false));
  }, [cursor, platform, status]);
  useEffect(() => { load(); }, [load]);

  const runJob = async (postFn, msg) => {
    setBusy(true);
    try {
      const { data } = await postFn();
      if (data.job_id) { const j = await pollJob(data.job_id); if (j.status === "Failed") toast.error(jobErrorMessage(j)); else toast.success(msg); }
      else toast.success(msg);
      setSel(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Failed"); }
    finally { setBusy(false); }
  };
  const cancel = (s) => runJob(() => api.post(`/marketing/publishing/${s.publishing_id}/cancel`, null, idemHeaders(newIdemKey())), "Cancellation requested");
  const publishNow = (s) => runJob(() => api.post(`/marketing/schedule/publish-now`, { content_id: s.content_id, channel_id: s.channel_id }, idemHeaders(newIdemKey())), "Publishing now");
  const retry = (s) => runJob(() => api.post(`/marketing/publishing/${s.publishing_id}/retry`, null, idemHeaders(newIdemKey())), "Retry started");
  const reschedule = (s) => runJob(() => api.post(`/marketing/publishing/${s.publishing_id}/reschedule`, { scheduled_at: new Date(reschedAt).toISOString() }, idemHeaders(newIdemKey())), "Rescheduled");

  if (slots === null) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#581C87]" /></div>;
  if (slots === false) return <p className="text-sm text-red-600">Failed to load calendar.</p>;

  const first = startOfMonth(cursor);
  const startPad = first.getDay();
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
  const byDay = {};
  slots.forEach((s) => { const k = (s.scheduled_at || "").slice(0, 10); (byDay[k] = byDay[k] || []).push(s); });

  return (
    <div data-testid="calendar-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold">Content Calendar</h2>
          <p className="text-sm text-gray-500 mt-1">Scheduled posts connected to real publishing records and Buffer channels.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant={view === "month" ? "default" : "outline"} size="sm" onClick={() => setView("month")} className={view === "month" ? "bg-[#581C87] text-white" : ""} data-testid="view-month"><CalendarDays className="h-4 w-4 mr-1" /> Month</Button>
          <Button variant={view === "list" ? "default" : "outline"} size="sm" onClick={() => setView("list")} className={view === "list" ? "bg-[#581C87] text-white" : ""} data-testid="view-list"><List className="h-4 w-4 mr-1" /> List</Button>
          <Button variant="outline" size="sm" onClick={() => setCursor(startOfMonth(new Date()))} data-testid="today-button">Today</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-4">
        <Button variant="ghost" size="icon" onClick={() => setCursor(addMonths(cursor, -1))} data-testid="prev-month"><ChevronLeft className="h-4 w-4" /></Button>
        <span className="font-heading font-bold w-40 text-center">{cursor.toLocaleString("default", { month: "long", year: "numeric" })}</span>
        <Button variant="ghost" size="icon" onClick={() => setCursor(addMonths(cursor, 1))} data-testid="next-month"><ChevronRight className="h-4 w-4" /></Button>
        <Select value={platform} onValueChange={setPlatform}><SelectTrigger className="w-40" data-testid="cal-filter-platform"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All platforms</SelectItem>{["Instagram", "Facebook", "LinkedIn", "YouTube", "TikTok"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-44" data-testid="cal-filter-status"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All statuses</SelectItem>{Object.keys(STATUS_COLORS).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
      </div>

      {view === "month" ? (
        <div className="mt-4 grid grid-cols-7 gap-1" data-testid="calendar-month">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="text-xs font-semibold text-gray-400 px-2 py-1">{d}</div>)}
          {cells.map((c, i) => (
            <div key={i} className={`min-h-[92px] rounded-lg border p-1 ${c ? "border-gray-100 bg-white" : "border-transparent"}`}>
              {c && <div className="text-xs text-gray-400 px-1">{c.getDate()}</div>}
              {c && (byDay[ymd(c)] || []).map((s) => (
                <button key={s.id} onClick={() => { setSel(s); setReschedAt(""); }} className={`w-full text-left mt-1 rounded px-1.5 py-1 text-[11px] truncate ${STATUS_COLORS[s.publishing_status] || "bg-gray-100"}`} data-testid={`cal-slot-${s.id}`} title={s.hook}>
                  {s.failure && <AlertTriangle className="h-3 w-3 inline mr-0.5 text-red-500" />}{s.platform}: {s.hook}
                </button>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-4 bg-white rounded-xl border border-gray-200 divide-y divide-gray-50" data-testid="calendar-list">
          {slots.length === 0 && <p className="text-sm text-gray-400 px-6 py-10 text-center" data-testid="calendar-empty">No scheduled posts. Use Build Weekly Schedule on the Publishing page or schedule from Content.</p>}
          {slots.map((s) => (
            <button key={s.id} onClick={() => { setSel(s); setReschedAt(""); }} className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-gray-50" data-testid={`cal-row-${s.id}`}>
              {s.media_thumb ? <img src={mediaFileUrl(s.media_thumb.replace("/api/marketing/media/file/", ""))} alt="" className="h-12 w-10 object-cover rounded" /> : <div className="h-12 w-10 bg-gray-50 rounded" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{s.hook}</p>
                <p className="text-xs text-gray-400">{s.local_time} · {s.platform} · {s.content_format}</p>
              </div>
              <span className={`rounded-full px-2 py-0.5 text-xs ${STATUS_COLORS[s.publishing_status] || "bg-gray-100"}`}>{s.publishing_status}</span>
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-md" data-testid="slot-dialog">
          <DialogHeader><DialogTitle className="font-heading">{sel?.platform} · {sel?.publishing_status}</DialogTitle><DialogDescription>{sel?.local_time}</DialogDescription></DialogHeader>
          {sel && (
            <div className="space-y-3 py-1 text-sm">
              <p className="font-medium">{sel.hook}</p>
              {sel.failure && <p className="text-red-600 text-xs flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> {sel.failure}</p>}
              <Button variant="link" className="px-0 h-auto text-[#581C87]" onClick={() => nav(`/marketing/content/${sel.content_id}`)} data-testid="slot-open-content">Open content / media detail →</Button>
              <div className="flex items-center gap-2">
                <Input type="datetime-local" value={reschedAt} onChange={(e) => setReschedAt(e.target.value)} className="text-xs" data-testid="reschedule-input" />
                <Button size="sm" variant="outline" disabled={busy || !reschedAt} onClick={() => reschedule(sel)} data-testid="reschedule-button">Reschedule</Button>
              </div>
            </div>
          )}
          <DialogFooter className="flex-wrap gap-2">
            {sel?.publishing_status === "Failed" && <Button size="sm" onClick={() => retry(sel)} disabled={busy} className="bg-[#581C87] text-white" data-testid="slot-retry">Retry</Button>}
            {["Scheduled", "Ready to Schedule"].includes(sel?.publishing_status) && <Button size="sm" onClick={() => publishNow(sel)} disabled={busy} variant="outline" data-testid="slot-publish-now">Publish Now</Button>}
            {["Scheduled", "Ready to Schedule", "Sending"].includes(sel?.publishing_status) && <Button size="sm" onClick={() => cancel(sel)} disabled={busy} variant="outline" className="text-red-600" data-testid="slot-cancel">Cancel</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
